import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '@/components/base/StatCard';
import { onlineRequests, type OnlineAppointmentRequest, type RequestStatus } from '@/mocks/online_requests';

const allStatuses: RequestStatus[] = ["Onay Bekliyor", "Onaylandı", "Reddedildi", "Randevuya Dönüştü"];

export default function OnlineRequestsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [requestList, setRequestList] = useState<OnlineAppointmentRequest[]>(onlineRequests);
  const [detailItem, setDetailItem] = useState<OnlineAppointmentRequest | null>(null);

  const filtered = useMemo(() => {
    let data = [...requestList];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      data = data.filter(r => r.customerName.toLowerCase().includes(q) || r.phone.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') data = data.filter(r => r.status === statusFilter);
    return data.sort((a, b) => b.requestDate.localeCompare(a.requestDate));
  }, [requestList, searchQuery, statusFilter]);

  const handleStatusChange = (id: string, newStatus: RequestStatus) => {
    setRequestList(prev => prev.map(r => r.id === id ? { ...r, status: newStatus, handledBy: 'Seda Avcı', handledAt: '2026-05-08 10:00' } : r));
  };

  const stats = useMemo(() => ({
    total: requestList.length,
    pending: requestList.filter(r => r.status === 'Onay Bekliyor').length,
    approved: requestList.filter(r => r.status === 'Onaylandı').length,
    converted: requestList.filter(r => r.status === 'Randevuya Dönüştü').length,
  }), [requestList]);

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-display" style={{ color: 'var(--text-primary)' }}>Online Randevu Talepleri</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{filtered.length} talep kaydı</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Toplam', value: stats.total, icon: 'ri-global-line', color: '#C9A962' },
          { label: 'Bekleyen', value: stats.pending, icon: 'ri-time-line', color: '#F59E0B' },
          { label: 'Onaylandı', value: stats.approved, icon: 'ri-check-line', color: '#10B981' },
          { label: 'Dönüştü', value: stats.converted, icon: 'ri-calendar-check-line', color: '#3B82F6' },
        ].map(s => (
          <div key={s.label} className="spa-card p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-spa flex items-center justify-center flex-shrink-0" style={{ background: s.color + '12' }}>
              <div className="w-5 h-5 flex items-center justify-center"><i className={`${s.icon} text-lg`} style={{ color: s.color }} /></div>
            </div>
            <div>
              <p className="text-lg font-bold font-display" style={{ color: 'var(--text-primary)' }}>{s.value}</p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center"><i className="ri-search-line text-sm" style={{ color: 'var(--text-muted)' }} /></div>
          <input className="spa-input pl-10" placeholder="Müşteri adı veya telefon ara..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
        <div className="flex gap-1 p-1 rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
          {[
            { key: 'all', label: 'Tümü' },
            { key: 'Onay Bekliyor', label: 'Bekleyen' },
            { key: 'Onaylandı', label: 'Onaylandı' },
            { key: 'Randevuya Dönüştü', label: 'Dönüştü' },
            { key: 'Reddedildi', label: 'Reddedildi' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all"
              style={{
                background: statusFilter === f.key
                  ? 'linear-gradient(135deg, var(--gold), var(--gold-dark))'
                  : 'transparent',
                color: statusFilter === f.key ? '#fff' : 'var(--text-secondary)',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Request Cards */}
      <div className="grid grid-cols-1 gap-3">
        {filtered.map(req => (
          <div key={req.id} className="spa-card p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white flex-shrink-0" style={{ background: req.status === 'Onay Bekliyor' ? '#F59E0B' : req.status === 'Onaylandı' ? '#10B981' : req.status === 'Reddedildi' ? '#EF4444' : '#3B82F6' }}>
                  {req.customerName.charAt(0)}
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{req.customerName}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{req.phone} | {req.source}</p>
                </div>
              </div>
              <StatusBadge status={req.status}>{req.status}</StatusBadge>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <p style={{ color: 'var(--text-muted)' }}>Hizmet</p>
                <p style={{ color: 'var(--text-primary)' }}>{req.service}</p>
              </div>
              <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <p style={{ color: 'var(--text-muted)' }}>Tercih</p>
                <p style={{ color: 'var(--text-primary)' }}>{req.preference} | {req.timePreference}</p>
              </div>
              <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <p style={{ color: 'var(--text-muted)' }}>Kişi Sayısı</p>
                <p style={{ color: 'var(--text-primary)' }}>{req.personCount} kişi</p>
              </div>
              <div className="p-2 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                <p style={{ color: 'var(--text-muted)' }}>Talep Tarihi</p>
                <p style={{ color: 'var(--text-primary)' }}>{req.requestDate}</p>
              </div>
            </div>

            {req.notes && (
              <div className="p-2 rounded-spa-sm text-xs" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                <i className="ri-message-3-line mr-1" />{req.notes}
              </div>
            )}

            {req.handledBy && (
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                İşlem: {req.handledBy} | {req.handledAt}
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
              {req.status === 'Onay Bekliyor' && (
                <>
                  <button onClick={() => handleStatusChange(req.id, 'Onaylandı')} className="px-3 py-2 rounded-spa-sm text-xs font-medium cursor-pointer" style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}>
                    <i className="ri-check-line mr-1" />Onayla
                  </button>
                  <button onClick={() => handleStatusChange(req.id, 'Randevuya Dönüştü')} className="px-3 py-2 rounded-spa-sm text-xs font-medium cursor-pointer" style={{ background: 'rgba(59,130,246,0.12)', color: '#3B82F6' }}>
                    <i className="ri-calendar-check-line mr-1" />Randevuya Çevir
                  </button>
                  <button onClick={() => handleStatusChange(req.id, 'Reddedildi')} className="px-3 py-2 rounded-spa-sm text-xs font-medium cursor-pointer" style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444' }}>
                    <i className="ri-close-line mr-1" />Reddet
                  </button>
                </>
              )}
              <button onClick={() => setDetailItem(req)} className="spa-btn-secondary text-xs py-2 px-3">
                <i className="ri-eye-line mr-1" />Detay
              </button>
              <a href={`https://wa.me/${req.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded-spa-sm text-xs font-medium cursor-pointer flex items-center gap-1" style={{ background: 'rgba(16,185,129,0.12)', color: '#10B981' }}>
                <i className="ri-whatsapp-line" />WhatsApp
              </a>
              <button onClick={() => navigate('/musteriler')} className="px-3 py-2 rounded-spa-sm text-xs font-medium cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                <i className="ri-user-add-line mr-1" />Müşteri Kartı
              </button>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
            <i className="ri-global-line text-xl" style={{ color: 'var(--text-muted)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Online randevu talebi bulunamadı</p>
        </div>
      )}

      {/* Detail Modal */}
      {detailItem && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0" style={{ background: 'var(--modal-overlay)' }} onClick={() => setDetailItem(null)} />
          <div className="relative bg-theme-card rounded-spa w-full max-w-md flex flex-col max-h-[90vh] animate-scale-in border overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
            <div className="flex items-center justify-between p-5 flex-shrink-0">
              <h3 className="text-lg font-semibold font-display" style={{ color: 'var(--text-primary)' }}>Talep Detayı</h3>
              <button onClick={() => setDetailItem(null)} className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="px-5 pb-5 space-y-3 text-sm overflow-y-auto flex-1 min-h-0">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Müşteri</p><p style={{ color: 'var(--text-primary)' }}>{detailItem.customerName}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Telefon</p><p style={{ color: 'var(--text-primary)' }}>{detailItem.phone}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Hizmet</p><p style={{ color: 'var(--text-primary)' }}>{detailItem.service}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Kaynak</p><p style={{ color: 'var(--text-primary)' }}>{detailItem.source}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Tarih Tercihi</p><p style={{ color: 'var(--text-primary)' }}>{detailItem.preference}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Saat Tercihi</p><p style={{ color: 'var(--text-primary)' }}>{detailItem.timePreference}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Kişi Sayısı</p><p style={{ color: 'var(--text-primary)' }}>{detailItem.personCount}</p></div>
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}><p className="text-xs" style={{ color: 'var(--text-muted)' }}>Durum</p><StatusBadge status={detailItem.status}>{detailItem.status}</StatusBadge></div>
              </div>
              {detailItem.notes && (
                <div className="p-3 rounded-spa-sm" style={{ background: 'var(--bg-tertiary)' }}>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Not</p>
                  <p style={{ color: 'var(--text-primary)' }}>{detailItem.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}